/* eslint-disable @typescript-eslint/no-unused-vars,  @typescript-eslint/ban-types */
import { default as Axios, AxiosResponse } from "axios";
import { WithTimestamps } from "@/typing";
import { MonthIndex } from "@/typing";
import { Submission } from "@/entities/documentation";
import {
  IFrameContent,
  SubmissionKind,
  FileContent,
  TableContent,
  CommentContent,
  SubmissionContent,
} from "@/entities/documentation/submission";
import { PagedAsyncIterDriver, Paginated } from "@/pagination";
import HttpService from "./http-service";
import { AsyncPaginatedRequest, submissionService } from ".";

import { LazyAsyncIter } from "@/iterators";
import { pageMapperBuilder } from "@/pagination/paginated";
import MonthPeriod from "@/entities/month-period";
import FailedValidation, {
  isFailedValidationProblem,
} from "@/error/api/failed-validation";
import ApiPaginated, { toLocalPaginated } from "./paginated";

const PROCESS_POLLING_RATE = 10_000;
interface SubmissionProcess {
  status: "completed" | "failed" | "in-progress" | null | "dispatched" | "canceled";
  result: unknown,
}
function retryUntil(promiseGenerator: () => Promise<boolean>, retryDelay: number) {
  return new Promise((resolve, reject) => promiseGenerator()
    .then((done) => {
      if (!done) {
        setTimeout(
          () => retryUntil(promiseGenerator, retryDelay).then(resolve).catch(reject),
          retryDelay
        );
      } else {
        resolve(done);
      }
    }).catch(reject));
}

type UpdateProgress = (progress: SubmissionProgressEvent["progress"], step?: SubmissionProgressEvent["step"]) => void;

export default class DocumentationSubmissionService extends HttpService {
  protected requestMaxSize: number = 100 * 1_000_000; //  MB;
  protected singleRequestMaxFileCount = 15;
  public getSubmissions(params: {
    entityDocsIds?: number[];
    period?: MonthPeriod;
    entityDepartmentIds?: number[];
  }): AsyncIterator<Paginated<Submission>> {
    const reqParams: Record<string, string | number> = {};
    if (params.entityDocsIds) {
      reqParams.entity_docs_ids = params.entityDocsIds.join(",");
    }
    if (params.entityDepartmentIds) {
      reqParams.entity_deparments_ids = params.entityDepartmentIds.join(",");
    }
    if (params.period) {
      reqParams.period = `${params.period.year}-${params.period.monthIndex}`;
    }
    const iter = new AsyncPaginatedRequest<SubmissionDTO>(
      "get",
      "submissions",
      reqParams,
      undefined,
      this.httpClient
    )[Symbol.asyncIterator]();
    return new LazyAsyncIter(iter).map(pageMapperBuilder(toSubmission));
  }

  public getEntitySubmissions(
    params: GetEntitySubmissionsParams
  ): Promise<AxiosResponse<SubmissionDTO[]>> {
    const { entity_id, ...reqParams } = {
      ...params,
      with_entity_documentation: 1,
    };
    return this.httpClient.get<SubmissionDTO[]>(
      `entities/${entity_id}/submissions`,
      { params: reqParams }
    );
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async getEntityisMunicipality(entity_documentation_id: number): Promise<any> {
    const response = await this.httpClient.get(
      `check/entity/type/${entity_documentation_id}`
    );
    //console.log(response.data.is_municipality)
    return response.data?.is_municipality;
  }
  async checkIfSubmissionCanBeDeletedOrDisabled(submission_id: number): Promise<boolean> {
    const response = await this.httpClient.get(`check/submissions/can-be-deleted-or-disabled/${submission_id}`);
    return response.data?.can_be_deleted_or_disabled;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async verifyIsEntityIsCommunalBoard(entity_documentation_id: number): Promise<any> {
    const response = await this.httpClient.get(
        `verify/entity/type/${entity_documentation_id}/communal-board`
    );
    return response.data?.is_communal_board;
  }

  public getSubmission(params: {
    id: number;
  }): Promise<AxiosResponse<SubmissionDTO>> {
    return this.httpClient.get<SubmissionDTO>(`submissions/${params.id}`);
  }

  public deleteSubmission(params: {
    id: number;
  }): Promise<AxiosResponse<void>> {
    return this.httpClient.delete(`submissions/${params.id}`);
  }

  public updateSubmission(params: {
    id: number;
    enabled: boolean;
  }): Promise<AxiosResponse<void>> {
    return this.httpClient.patch(`submissions/${params.id}`, {
      ...params,
      id: undefined,
    });
  }

  private async submitByChunk(
    params: SubmitDocumentationWithContent,
    updateProgress: UpdateProgress,
    abortSignal?: AbortSignal,
  ): Promise<number> {
    let submissionId: null | number = null;
    let contentBuffer: SubmissionContentCreate[] = [];
    let reqSent = 0;
    const flushBuffer = async (
      isPartial: boolean,
      isLast: boolean,
      reqSubmissionId?: number | null
    ) => {
      const formData = this.createFormData(
        params.entityDocumentationId,
        params.period,
        contentBuffer,
        params.entityDepartmentId
      );
      if (reqSent > 0) {
        formData.delete("entity_documentation_id");
      }
      submissionId = await this.sendContentSubmission(
        formData,
        isPartial,
        isLast,
        reqSubmissionId,
        abortSignal,
      );
      contentBuffer = [];
      reqSent += 1;
    };
    let cSize = 0;
    let fileCount = 0;
    let cChunksCurrIndex = 0;
    const contentChunks: SubmissionContentCreate[][] = [[]];
    params.content.forEach((content) => {
      if (isFileContentCreate(content)) {
        cSize += content.file.size;
        if (cSize > this.requestMaxSize || ++fileCount > this.singleRequestMaxFileCount) {
          cChunksCurrIndex += 1;
          contentChunks[cChunksCurrIndex] = [];
          cSize = content.file.size;
        }
      }
      contentChunks[cChunksCurrIndex].push(content);
    });

    for (const [index, chunk] of contentChunks.entries()) {
      contentBuffer = chunk;
      const isPartial = contentChunks.length > 1;
      const isLast = index >= contentChunks.length - 1;
      await flushBuffer(isPartial, isLast, submissionId);
      updateProgress(
        (index + 1) / contentChunks.length,
        "uploading"
      );
    }
    if (submissionId === null) {
      throw new Error("submissionId should not be null while sending submission with content");
    }
    return submissionId;
  }

  public async submitDocumentation(
    params: SubmitDocumentationParams,
    submissionProgressHook?: (progressEvent: SubmissionProgressEvent) => void,
    abortSignal?: AbortSignal,
  ): Promise<number | null> {
    let submissionId: null | number = null;
    const updateProgress = (progress: SubmissionProgressEvent["progress"], step: SubmissionProgressEvent["step"] = undefined) => {
      if (submissionProgressHook != null) {
        submissionProgressHook({ progress, submissionId: submissionId ?? undefined, step });
      }
    }
    updateProgress(0, "uploading");

    try {
      if (params.isSupportedByPrev) {
        return await this.sendSupportedSubmission(params, abortSignal);
      }
      submissionId = await this.submitByChunk(params, updateProgress, abortSignal);
      updateProgress(undefined);

      await retryUntil(async () => {
        if (abortSignal?.aborted) {
          throw new Error("Aborted");
        }
        const { status, result } = await this.getSubmissionProgress(submissionId as number)
        updateProgress(null, status);
        if (status == "failed") {
          if (isFailedValidationProblem(result)) {
            throw new FailedValidation(result);
          }
          throw new Error("Submission processing failed");

        }
        if (status == "canceled") {
          throw new Error("La entrega fue cancelada");
        }
        return status == "completed";
      }
        , PROCESS_POLLING_RATE)
        .catch(async (e) => {
          if (submissionId !== null) {
            try {
              await this.cancel(submissionId);
            } catch {/* swallow */ }
          }
          throw e;
        });
      return submissionId;
    } catch (err) {

      if (Axios.isAxiosError(err)) {
        if (
          err.response &&
          isFailedValidationProblem(err.response.data)
        ) {
          throw new FailedValidation(err.response.data);
        }
      }
      updateProgress(null, "failed");
      throw err;
    }
    finally {
      updateProgress(null);
    }
  }

  public async getSubmissionProgress(submissionId: number): Promise<SubmissionProcess> {
    const url = `submissions/${submissionId}/process`;
    const response = await this.httpClient.get<SubmissionProcess>(url);
    return response.data;
  }



  public async cancel(submissionId: number): Promise<void> {
    const url = `submissions/${submissionId}/cancel`;
    await this.httpClient.post(url);
  }

  protected async sendSupportedSubmission(
    params: SubmitSupportedDocumentation,
    abortSignal?: AbortSignal,
  ): Promise<number> {
    const response = await this.httpClient.post("submissions", {
      entity_documentation_id: params.entityDocumentationId,
      period: params.period.toYMStr(),
      last_content: true,
      support_by_prev: params.isSupportedByPrev,
    }, { signal: abortSignal });
    // submission id
    return response.data;
  }

  protected async sendContentSubmission(
    formData: FormData,
    isPartial = false,
    isLast = true,
    submissionId: number | null = null,
    abortSignal?: AbortSignal,
  ): Promise<number> {
    formData.append("partial", isPartial ? "1" : "0");
    formData.append("last_content", isLast ? "1" : "0");
    if (submissionId != undefined) {
      formData.append("submission_id", submissionId.toString());
    }
    const response = await this.httpClient.post("submissions", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      signal: abortSignal
    });
    //SubmissionId
    return response.data;
  }

  protected createFormData(
    entityDocumentationId: number,
    period: MonthPeriod,
    content: SubmissionContentCreate[],
    entityDepartmentId?: number
  ): FormData {
    const formData = new FormData();
    const fileDiscriminants: Set<string> = new Set();
    formData.append("period", period.toYMStr());
    content.forEach((e) => {
      if (e.kind == "file") {
        if (e.fileDiscriminant) {
          formData.append(`${e.fileDiscriminant}[]`, e.file);
          fileDiscriminants.add(e.fileDiscriminant);
        } else {
          formData.append("files[]", e.file);
        }
      }
      if (e.kind == "iframe") {
        formData.append("iframes[]", e.title + "@@@" + e.link);
      }
      if (e.kind == "rich-text") {
        formData.append("comments", e.text);
      }
    });
    if (fileDiscriminants.size > 0) {
      for (const fileDiscriminant of fileDiscriminants.values()) {
        formData.append("file_discriminants[]", fileDiscriminant);
      }
    }
    if (entityDepartmentId != null) {
      formData.append("entity_department_id", entityDepartmentId.toString());
    }

    formData.append(
      "entity_documentation_id",
      entityDocumentationId.toString()
    );
    return formData;
  }

  public async getTableContentPage<T>(
    link: string, page: number, filter?: string, all = false
  ): Promise<Paginated<T>> {
    if (all) {
      const driver = PagedAsyncIterDriver.fromAsyncIterable<T>(this.getTableContent(link));
      const pageResult = await driver.fetchRemaining();

      return {
        currentPage: 1,
        perPage: pageResult.length,
        lastPage: 1,
        pageElements: pageResult,
        total: pageResult.length
      };
    }

    const response = await this.httpClient.get<ApiPaginated<T>>(link, { params: { page, filter } });

    return toLocalPaginated(response.data);
  }
  public getTableContent<T>(link: string): AsyncIterable<Paginated<T>> {
    return new AsyncPaginatedRequest<T>(
      "get",
      link,
      undefined,
      undefined,
      this.httpClient
    );
  }

  public async canSubmit(
    period: MonthPeriod,
    entityDocumentationIds?: number[]
  ): Promise<boolean | Array<[string, boolean]>> {
    const params = {
      period: period.toYMStr(),
      entity_documentation_ids: undefined as undefined | string,
    };
    if (entityDocumentationIds) {
      params.entity_documentation_ids = entityDocumentationIds.join(",");
    }
    const { data } = await this.httpClient.get<
      boolean | Record<number, boolean>
    >("submissions-can-submit", { params });
    if (typeof data === "boolean" || typeof data === "number") {
      return !!data;
    } else {
      return Object.entries(data);
    }
  }
}

export interface SubmissionProgressEvent {
  submissionId?: number;
  step?: SubmissionProcess["status"] | "uploading";
  progress: number | undefined | null;
}

interface ContentCreate {
  kind: SubmissionKind;
}
export interface IFrameContentCreate extends ContentCreate {
  kind: "iframe";
  link: string;
  title: string;
}

export interface TableContentCreate extends ContentCreate {
  kind: "table";
  tableDiscriminant?: string;
  file: File;
}

export interface FileContentCreate extends ContentCreate {
  kind: "file";
  fileDiscriminant?: string;
  file: File;
}

interface RichTextContentCreate extends ContentCreate {
  kind: "rich-text";
  text: string;
}
export type SubmissionContentCreate =
  | IFrameContentCreate
  | TableContentCreate
  | FileContentCreate
  | RichTextContentCreate;
function isIframeContentCreate(
  obj: SubmissionContentCreate
): obj is IFrameContentCreate {
  return obj.kind === "iframe";
}

function isTableContentCreate(
  obj: SubmissionContentCreate
): obj is TableContentCreate {
  return obj.kind === "table";
}

function isFileContentCreate(
  obj: SubmissionContentCreate
): obj is FileContentCreate {
  return obj.kind === "file";
}

function isRichTextContentCreate(
  obj: SubmissionContentCreate
): obj is RichTextContentCreate {
  return obj.kind === "rich-text";
}

export interface SubmitDocumentationBase {
  entityDocumentationId: number;
  period: MonthPeriod;
  entityDepartmentId?: number;
}

export type SubmitDocumentationWithContent = SubmitDocumentationBase & {
  content: SubmissionContentCreate[];
  isSupportedByPrev?: false;
};

export type SubmitSupportedDocumentation = SubmitDocumentationBase & {
  isSupportedByPrev: true;
};

export type SubmitDocumentationParams =
  | SubmitDocumentationWithContent
  | SubmitSupportedDocumentation;

export interface GetEntitySubmissionsParams {
  entity_id: string | number;
  period_year?: number;
  period_month?: MonthIndex;
  enabled?: boolean | number;
}

export interface GetSubmissionParams extends GetEntitySubmissionsParams {
  entity_ids?: number[];
  documentation_id: string | number;
}
export type ContentDTO = (IFrameDTO | DocumentDTO | TableDTO | CommentDTO) & {
  discriminant?: string;
};

export interface SubmissionDTO extends WithTimestamps {
  id: number;
  enabled: boolean;
  entity_documentation_id: number;
  submission_type_id: number;
  period_month: MonthIndex;
  period_year: number;
  entity_department_id: number;
  entity_documentation: {
    documentation_id: number;
    entity_id: number;
  };
  content: ContentDTO[];
}

export interface HasIFrameDTO {
  content: IFrameDTO;
}
export interface HasDocumentDTO {
  content: DocumentDTO;
}
export interface HasTableDTO {
  content: TableDTO;
}

export type ContentKind = "iframe" | "comment" | "file" | "table";
export interface ContentBaseDTO extends WithTimestamps {
  discriminant?: string;
  kind: ContentKind;
}
export interface IFrameDTO extends ContentBaseDTO {
  id: number;
  link: string;
  title: string;
  kind: "iframe";
}

export interface DocumentDTO extends ContentBaseDTO {
  id: number;
  kind: "file";
  filename: string;
  submission_id: number;
}
export interface CommentDTO extends ContentBaseDTO {
  id: number;
  kind: "comment";
  text: string;
}

function isIFrameDTO(obj: ContentDTO): obj is IFrameDTO {
  return obj.kind === "iframe";
}

function isDocumentDTO(obj: ContentDTO): obj is DocumentDTO {
  return obj.kind === "file";
}

function isTableDTO(obj: ContentDTO): obj is TableDTO {
  return obj.kind === "table";
}

function isCommentDTO(obj: ContentDTO): obj is CommentDTO {
  return obj.kind === "comment";
}

function hasIFrameDTO<T extends SubmissionDTO>(
  obj: T
): obj is T & HasIFrameDTO {
  return obj.submission_type_id === 3;
}

function hasDocumentDTO<T extends SubmissionDTO>(
  obj: T
): obj is T & HasDocumentDTO {
  return obj.submission_type_id === 1;
}

function hasTableDTO<T extends SubmissionDTO>(obj: T): obj is T & HasTableDTO {
  return obj.submission_type_id === 2;
}
export interface TableDTO extends WithTimestamps {
  kind: "table";
  data_link: string;
  download_link: string;
  columns: string[] | Record<string, string>;
  total_rows: number;
  id: number;
  features: Array<"filter" | "sort">
}

export function toSubmission(dto: SubmissionDTO): Submission {
  const content = dto.content.map((el): SubmissionContent => {
    if (isIFrameDTO(el)) {
      return new IFrameContent(el.link, el.title);
    }

    if (isCommentDTO(el)) {
      return new CommentContent(el.text);
    }
    if (isDocumentDTO(el)) {
      // This needs the absolute path because it is a link.
      const link = `/api/submissions/${el.submission_id}/files/${el.id}/download`;
      return new FileContent(link, el.filename);
    }
    if (isTableDTO(el)) {
      let columns;
      if (Array.isArray(el.columns)) {
        columns = el.columns.map((e) => ({ name: e }));
      } else {
        columns = Array.from(Object.entries(el.columns), ([key, val]) => ({
          name: key,
          type: val,
        }));
      }
      const downloadLink =
        el.download_link === "" ? undefined : el.download_link;
      const tableContent = new TableContent(
        { columns },
        (page: number, filter?: string, all = false) =>
          submissionService.getTableContentPage(el.data_link, page, filter, all),
        el.total_rows,
        el.features,
        downloadLink,
      );
      tableContent.discriminant = el.discriminant;
      return tableContent;
    }
    throw new Error("Unsupported content type.");
  });
  return new Submission(
    dto.id,
    content,
    dto.entity_documentation_id,
    dto.entity_department_id,
    new Date(Date.parse(dto.created_at)),
    dto.enabled
  );
}
